import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText, Hash, Mail, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch, Textarea } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { EmailStatus } from './record-tools';
import {
  recordPath,
  useDeleteTemplate,
  useEmailStats,
  useMergeFields,
  useSaveTemplate,
  useTemplates,
  useTrackedEmails,
  type SalesTemplate,
} from './api';

/**
 * Sales email (HubSpot): everything sent from records and sequences with its
 * opens, clicks and replies, and the team's templates and snippets.
 */
export function EmailsPage() {
  const [tab, setTab] = useState<'sent' | 'TEMPLATE' | 'SNIPPET'>('sent');

  return (
    <div>
      <PageHeader
        title="Emails & templates"
        description="Every email sent from the CRM with who opened, clicked and replied — and the templates and #snippets the team reuses."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Emails & templates' }]}
      />
      <div className="mb-4 flex gap-1 border-b">
        {(
          [
            ['sent', 'Sent emails', Mail],
            ['TEMPLATE', 'Templates', FileText],
            ['SNIPPET', 'Snippets', Hash],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm',
              tab === key ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>
      {tab === 'sent' ? <SentEmails /> : <TemplateLibrary kind={tab} />}
    </div>
  );
}

function SentEmails() {
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);
  const { data: stats } = useEmailStats(30);
  const { data, isLoading } = useTrackedEmails({ mine, page });
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {(
            [
              ['Sent (30 days)', stats.sent, null],
              ['Opened', stats.opened, stats.openRate],
              ['Clicked', stats.clicked, stats.clickRate],
              ['Replied', stats.replied, stats.replyRate],
            ] as const
          ).map(([label, value, rate]) => (
            <Card key={label}>
              <CardContent className="pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-2xl font-extrabold">
                  {value}
                  {rate !== null && <span className="ms-2 text-sm font-semibold text-primary">{rate}%</span>}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={mine} onCheckedChange={(value) => { setMine(value); setPage(1); }} /> Only mine
      </label>
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isLoading ? (
            <PageLoader />
          ) : !data?.items.length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No emails yet. Send one from any lead, contact or deal — or start a sequence.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((email) => {
                  const path = recordPath(email.entityType, email.entityId);
                  return (
                    <TableRow key={email.id}>
                      <TableCell className="max-w-xs">
                        <p className="truncate font-medium">{email.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          {email.sentBy?.name}
                          {email.enrollmentId ? ' · sequence' : ''}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm">
                        {path ? (
                          <Link className="text-link hover:underline" to={path}>
                            {email.toName || email.to}
                          </Link>
                        ) : (
                          email.toName || email.to
                        )}
                        {email.toName && <p className="text-xs text-muted-foreground">{email.to}</p>}
                      </TableCell>
                      <TableCell>
                        <EmailStatus email={email} />
                        {email.error && <p className="mt-1 max-w-xs truncate text-xs text-destructive">{email.error}</p>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(email.sentAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-muted-foreground">
                {page} / {pages}
              </span>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TemplateLibrary({ kind }: { kind: 'TEMPLATE' | 'SNIPPET' }) {
  const { data: rows = [], isLoading } = useTemplates(kind);
  const [editing, setEditing] = useState<SalesTemplate | 'new' | null>(null);
  const isSnippet = kind === 'SNIPPET';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {isSnippet
            ? 'Short blocks of text — type #shortcut and a space in any email to drop one in.'
            : 'Whole emails to start from. Placeholders fill from the record when sent.'}
        </p>
        <Can permission="crm.template.create">
          <Button onClick={() => setEditing('new')}>
            <Plus /> New {isSnippet ? 'snippet' : 'template'}
          </Button>
        </Can>
      </div>
      {isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          No {isSnippet ? 'snippets' : 'templates'} yet.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => (
            <Card key={row.id} className={cn(row.canEdit && 'cursor-pointer hover:shadow-md')} onClick={() => row.canEdit && setEditing(row)}>
              <CardContent className="space-y-2 pt-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">
                    {row.folder && <span className="text-muted-foreground">{row.folder} / </span>}
                    {row.name}
                  </p>
                  {row.shortcut && <Badge variant="secondary">#{row.shortcut}</Badge>}
                </div>
                {row.subject && <p className="truncate text-sm">{row.subject}</p>}
                <p className="line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">{row.body}</p>
                <p className="text-xs text-muted-foreground">
                  {row.isShared ? 'Team' : 'Only me'} · {row.owner?.name ?? '—'}
                  {!isSnippet && ` · used ${row.usageCount}× · ${row.sent ? Math.round((row.opened / row.sent) * 100) : 0}% opened`}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {editing && <TemplateEditor kind={kind} template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TemplateEditor({ kind, template, onClose }: { kind: 'TEMPLATE' | 'SNIPPET'; template: SalesTemplate | null; onClose: () => void }) {
  const save = useSaveTemplate();
  const remove = useDeleteTemplate();
  const { data: fields = [] } = useMergeFields();
  const [name, setName] = useState(template?.name ?? '');
  const [shortcut, setShortcut] = useState(template?.shortcut ?? '');
  const [folder, setFolder] = useState(template?.folder ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isShared, setIsShared] = useState(template?.isShared ?? true);
  const [error, setError] = useState<string | null>(null);
  const isSnippet = kind === 'SNIPPET';

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: template?.id, kind, name, shortcut, folder, subject, body, isShared });
      toast.success('Saved');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit' : 'New'} {isSnippet ? 'snippet' : 'template'}</DialogTitle>
          <DialogDescription>Placeholders: {fields.map((field) => `{{${field.key}}}`).join(' ')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            {isSnippet ? (
              <div className="space-y-1.5">
                <Label>Shortcut</Label>
                <Input value={shortcut} onChange={(event) => setShortcut(event.target.value.replace(/[^a-z0-9_-]/gi, '').toLowerCase())} placeholder="pricing" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Folder</Label>
                <Input value={folder} onChange={(event) => setFolder(event.target.value)} placeholder="Follow-ups" />
              </div>
            )}
          </div>
          {!isSnippet && (
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Text</Label>
            <Textarea rows={isSnippet ? 5 : 10} value={body} onChange={(event) => setBody(event.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isShared} onCheckedChange={setIsShared} /> Share with the team
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={cn(template && 'sm:justify-between')}>
          {template && (
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                if (!window.confirm('Delete this?')) return;
                remove.mutate(template.id, { onSuccess: onClose });
              }}
            >
              <Trash2 /> Delete
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending || !name.trim() || !body.trim()}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
